/**
 * Back stack: what the Android back button (and Escape on a keyboard) closes first.
 *
 * Without it, Capacitor's default walks the WebView history, so back steps through tab and settings history
 * instead of closing the sheet in front of you. Anything that can be dismissed (sheets, action sheets, menus,
 * detail panes, full-screen viewers) registers a handler while it's showing; back runs the topmost one.
 *
 * - LIFO: the most recently registered handler runs first. `priority` lifts a handler above later ones (a modal
 *   confirm over a sheet that re-registers, say). Equal priority falls back to registration order.
 * - A handler returns `false` when it didn't handle this press (a sheet already at its lowest detent); the
 *   next handler gets it.
 * - Nothing registered: `router.back()` when the router has a previous entry, otherwise the app minimizes.
 */
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { onScopeDispose, watch, type Ref } from 'vue'
import type { RouteLocationRaw, RouteLocationResolved, Router } from 'vue-router'
import { isNativePlatform } from '@/utils/platform'

export type BackSource = 'back' | 'escape'
/** Return `false` to pass the press to the next handler; anything else means handled. */
export type BackHandler = (source: BackSource) => boolean | void

export interface BackHandlerOptions {
    /** Higher runs first. Default 0. */
    priority?: number
}

interface Entry {
    fn: BackHandler
    priority: number
    seq: number
}

const entries: Entry[] = []
let seq = 0

/** Register a handler; returns the function that unregisters it. */
export function pushBackHandler(fn: BackHandler, opts: BackHandlerOptions = {}): () => void {
    const entry: Entry = { fn, priority: opts.priority ?? 0, seq: ++seq }
    entries.push(entry)
    installEscape()
    return () => {
        const at = entries.indexOf(entry)
        if (at >= 0) entries.splice(at, 1)
    }
}

/** Run handlers top-down until one handles the press. Returns whether one did. */
export function runBackHandlers(source: BackSource = 'back'): boolean {
    const ordered = [...entries].sort((a, b) => b.priority - a.priority || b.seq - a.seq)
    for (const entry of ordered) {
        let result: boolean | void
        try {
            result = entry.fn(source)
        } catch (err) {
            // A broken handler still consumed the press: never let a bug minimize the app.
            console.error('back handler failed', err)
            return true
        }
        if (result !== false) return true
    }
    return false
}

export function hasBackHandlers(): boolean {
    return entries.length > 0
}

/**
 * Register `fn` while `active` is true (a ref or a getter); unregisters when it turns false or the calling
 * scope (component) is disposed. Re-activating puts it back on top.
 */
export function useBackHandler(
    fn: BackHandler,
    active: Ref<boolean> | (() => boolean),
    opts: BackHandlerOptions = {},
): void {
    let off: (() => void) | null = null
    const stop = watch(
        typeof active === 'function' ? active : () => active.value,
        (on) => {
            off?.()
            off = on ? pushBackHandler(fn, opts) : null
        },
        { immediate: true },
    )
    onScopeDispose(() => {
        stop()
        off?.()
        off = null
    })
}

// ── Escape (web, and hardware keyboards on native) ──────────────────────────────────────────────────────
let escapeInstalled = false
function installEscape() {
    if (escapeInstalled || typeof window === 'undefined') return
    escapeInstalled = true
    // Bubble phase on window: an input or menu inside the sheet that handles Escape itself (and calls
    // preventDefault / stopPropagation) wins.
    window.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing || !entries.length) return
        if (runBackHandlers('escape')) {
            e.preventDefault()
            e.stopPropagation()
        }
    })
}

// ── Android back button ─────────────────────────────────────────────────────────────────────────────────
export type BackOutcome = 'handler' | 'router' | 'minimize'

/** One back press: the topmost handler, else the router's previous entry, else minimize. */
export function handleBackPress(router: Pick<Router, 'back'>, minimize: () => void = () => void App.minimizeApp()): BackOutcome {
    if (runBackHandlers('back')) return 'handler'
    const state = typeof window !== 'undefined' ? (window.history.state as { back?: unknown } | null) : null
    if (state?.back) {
        router.back()
        return 'router'
    }
    minimize()
    return 'minimize'
}

/**
 * Leave a pushed screen (a NativeStackPane's back) for `to`: when the previous history entry already is that
 * screen, go back to it, so back never pushes a fresh copy of the list on top of the detail (which made the
 * next Android back reopen the detail, forever). Otherwise replace the current entry. `isTarget` decides what
 * counts as `to` (default: the same path, ignoring query and hash).
 */
export function popOrReplace(
    router: Pick<Router, 'back' | 'replace' | 'resolve'>,
    to: RouteLocationRaw,
    isTarget?: (prev: RouteLocationResolved) => boolean,
): 'back' | 'replace' {
    const back = typeof window !== 'undefined' ? (window.history.state as { back?: unknown } | null)?.back : null
    if (typeof back === 'string') {
        try {
            const prev = router.resolve(back)
            if (isTarget ? isTarget(prev) : prev.path === router.resolve(to).path) {
                router.back()
                return 'back'
            }
        } catch {
            // An entry the router can't resolve: replace below.
        }
    }
    void router.replace(to)
    return 'replace'
}

let backButtonInstalled = false
/** Call once at boot (main.ts). Native only; a no-op on the web or without the App plugin. */
export async function initBackButton(router: Router): Promise<void> {
    if (backButtonInstalled || !isNativePlatform() || !Capacitor.isPluginAvailable('App')) return
    backButtonInstalled = true
    try {
        await App.addListener('backButton', () => {
            handleBackPress(router)
        })
    } catch {
        backButtonInstalled = false
    }
}

/** Test-only. */
export function __resetBackStackForTests(): void {
    entries.length = 0
}
